import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Spin } from 'antd'
import { filter, isEmpty, isNil, map, omitBy } from 'lodash'

import { showErrorNotification } from '../../../core/helpers'

import { Page } from '../../../common/components/sections'
import {
  StyledPageTitle,
  StyledTaskPageHeader,
} from '../../../common/components/styled'
import { APP_ROUTES } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks'

import { splitMenuDataByMenuTitle } from '../../helpers'
import { useMenuDataLazyQuery } from '../../hooks'
import { MenuDataForm } from '../forms'

const isEmptyValue = value =>
  isNil(value) || value === '' || (typeof value === 'object' && isEmpty(value))

const cleanObject = obj =>
  omitBy(obj, (value, key) => key === '__typename' || isEmptyValue(value))

const saveJsonlToFile = async (dataArray, suggestedFilename) => {
  // JSONL format: one JSON object per line
  const jsonlString = map(dataArray, item => JSON.stringify(item)).join('\n')
  const blob = new Blob([jsonlString], { type: 'application/x-ndjson' })

  try {
    const handle = await window.showSaveFilePicker({
      suggestedName: suggestedFilename,
      types: [
        {
          description: 'JSONL Files',
          accept: { 'application/x-ndjson': ['.jsonl'] },
        },
      ],
    })
    const writable = await handle.createWritable()
    await writable.write(blob)
    await writable.close()
    return true
  } catch (err) {
    if (err.name === 'AbortError') {
      return false
    }
    throw err
  }
}

const MenuDataPage = () => {
  useDocumentTitle(APP_ROUTES.MISC.children.REPORTS.children.MENU_DATA.label)

  const [fetchMenuData, { isLoading, error }] = useMenuDataLazyQuery()
  const [isSaved, setIsSaved] = useState(false)

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'Failed to fetch menu data',
        description:
          error?.message || 'An error occurred while fetching menu data',
      })
    }
  }, [error])

  const handleSubmit = useCallback(
    async (menuCurationTaskId, splitByMenuTitle) => {
      setIsSaved(false)
      const result = await fetchMenuData(menuCurationTaskId)

      const { menuData } = result?.data || {}

      if (menuData) {
        const brandName = menuData?.brand?.name || 'unknown-brand'
        const { menuCurationTaskId, menuId, menuCurationTaskUrl } = menuData

        const sanitizedBrandName = brandName.replace(/[^a-zA-Z0-9-_]/g, '-')

        const cleanedData = {
          dishes: map(menuData?.dishes, cleanObject),
          menuContext: map(menuData?.menuContext, cleanObject),
        }

        const metadata = {
          brandName,
          menuCurationTaskId,
          menuId,
          menuCurationTaskUrl,
        }

        let saved = false

        if (splitByMenuTitle) {
          // Split by menu title and save as JSONL
          const splitData = splitMenuDataByMenuTitle(cleanedData)
          const jsonlData = map(
            filter(splitData, ({ dishes }) => {
              if (isEmpty(dishes)) {
                return false
              }
              return true
            }),
            ({ dishes, menuContext }) => ({
              input: {
                dishes,
                menuContext,
              },
              metadata,
            }),
          )
          saved = await saveJsonlToFile(
            jsonlData,
            `${sanitizedBrandName}.jsonl`,
          )
        } else {
          // Save as single JSON file
          saved = await saveJsonlToFile(
            [{ input: cleanedData, metadata }],
            `${sanitizedBrandName}.jsonl`,
          )
        }

        setIsSaved(saved)
      }
    },
    [fetchMenuData],
  )

  return (
    <Page>
      <StyledTaskPageHeader sticky>
        <StyledPageTitle>Menu Data</StyledPageTitle>
      </StyledTaskPageHeader>

      <MenuDataForm onSubmit={handleSubmit} isLoading={isLoading} />

      {isLoading && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Spin tip="Fetching menu data..." />
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message="Error"
          description={error?.message || 'Failed to fetch menu data'}
          style={{ marginTop: 24 }}
        />
      )}

      {isSaved && (
        <Alert
          type="success"
          message="Success"
          description="Menu data has been saved successfully."
          style={{ marginTop: 16 }}
        />
      )}
    </Page>
  )
}

export default MenuDataPage
